import axios from "axios";
import Cookies from "js-cookie";

const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const USER_ROLE_KEY = "userRole";

const ACCESS_TOKEN_DAYS = 1;
const REFRESH_TOKEN_DAYS = 7;

export const isOwnerRole = (role = Cookies.get(USER_ROLE_KEY)) => {
  if (!role) return false;
  const normalized = String(role).toLowerCase();
  return normalized === "o" || normalized === "owner";
};

export const setAuthTokens = ({
  accessToken,
  refreshToken,
  role,
  accessExpires = ACCESS_TOKEN_DAYS,
  refreshExpires = REFRESH_TOKEN_DAYS,
} = {}) => {
  if (accessToken) {
    Cookies.set(ACCESS_TOKEN_KEY, accessToken, { expires: accessExpires });
  }
  if (refreshToken) {
    Cookies.set(REFRESH_TOKEN_KEY, refreshToken, { expires: refreshExpires });
  }
  if (role) {
    Cookies.set(USER_ROLE_KEY, role, { expires: refreshExpires });
  }
};

/**
 * Admin "login as owner" opens e.g.
 * /owner-account/ownerprofile#accessToken=...&refreshToken=...&role=O
 * Store tokens in cookies (same keys as owner OTP login), then clean the URL.
 */
export const applyAuthTokensFromUrl = () => {
  if (typeof window === "undefined") return false;

  const rawHash = window.location.hash.replace(/^#/, "");
  // Support both "#accessToken=..." and "#/?accessToken=..." styles
  const hashQuery = rawHash.includes("=")
    ? rawHash.replace(/^\//, "").replace(/^\?/, "")
    : "";
  const hashParams = new URLSearchParams(hashQuery);
  const queryParams = new URLSearchParams(window.location.search);

  const accessToken =
    hashParams.get("accessToken") || queryParams.get("accessToken");
  const refreshToken =
    hashParams.get("refreshToken") || queryParams.get("refreshToken");
  const role = hashParams.get("role") || queryParams.get("role") || "O";

  if (!accessToken) return false;

  setAuthTokens({
    accessToken,
    refreshToken,
    role,
    accessExpires: 7,
    refreshExpires: 7,
  });

  // Keep current path (e.g. /owner-account/ownerprofile), drop tokens from URL
  const cleanUrl = `${window.location.pathname}${window.location.search}`
    .replace(/[?&]accessToken=[^&]*/g, "")
    .replace(/[?&]refreshToken=[^&]*/g, "")
    .replace(/[?&]role=[^&]*/g, "")
    .replace(/\?&/, "?")
    .replace(/\?$/, "");

  window.history.replaceState(null, "", cleanUrl || window.location.pathname);
  return true;
};

export const clearAuthTokens = () => {
  Cookies.remove(ACCESS_TOKEN_KEY);
  Cookies.remove(REFRESH_TOKEN_KEY);
  Cookies.remove(USER_ROLE_KEY);
};

export const getLoginRedirectPath = () =>
  isOwnerRole() ? "/ownerlogin" : "/login";

/**
 * Calls the correct refresh-token API and updates cookies.
 * Returns the new access token.
 */
export const refreshAccessToken = async () => {
  const refreshToken = Cookies.get(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const endpoint = isOwnerRole()
    ? "/owner/refresh-token"
    : "/customer/auth/refresh-token";

  const { data } = await axios.post(
    `${process.env.REACT_APP_API_BUY_URL}${endpoint}`,
    { refreshToken },
    {
      headers: {
        "Content-Type": "application/json;charset=utf8",
        accept: "*/*",
      },
    },
  );

  const result = data?.result || data?.data?.result || data;
  const newAccessToken = result?.accessToken;
  const newRefreshToken = result?.refreshToken;

  if (!newAccessToken) {
    throw new Error("Refresh token response missing accessToken");
  }

  setAuthTokens({
    accessToken: newAccessToken,
    refreshToken: newRefreshToken || refreshToken,
    role: Cookies.get(USER_ROLE_KEY),
  });

  return newAccessToken;
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Shared 401 handler: refresh once, queue parallel requests, then retry.
 */
export const handleUnauthorizedResponse = async (error, axiosInstance) => {
  const originalRequest = error?.config;

  if (!error?.response || error.response.status !== 401 || !originalRequest) {
    return Promise.reject(error);
  }

  // Avoid infinite loop if refresh itself fails
  if (
    originalRequest.url?.includes("refresh-token") ||
    originalRequest._retry
  ) {
    clearAuthTokens();
    window.location.assign(getLoginRedirectPath());
    return Promise.reject(error);
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }).then((token) => {
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return axiosInstance(originalRequest);
    });
  }

  originalRequest._retry = true;
  isRefreshing = true;

  try {
    const newAccessToken = await refreshAccessToken();
    processQueue(null, newAccessToken);
    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
    return axiosInstance(originalRequest);
  } catch (refreshError) {
    processQueue(refreshError, null);
    clearAuthTokens();
    window.location.assign(getLoginRedirectPath());
    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
};
